import React, { useEffect, useRef } from 'react';
import { Box } from '../Box';
import styled, { useTheme } from 'styled-components';
import { useRefState } from '../hooks';
import { clamp, decimalClamp, stepClamp } from '../utils';
import { ColorVariants, OSKThemeType } from '../DefaultThemeProvider';
import { Typography } from '../Typography';
import SliderGrabber from './SliderGrabber';
import { TextInput } from '../TextInput';

type SliderProps = {
    /** The lowest value the user can select */
    minValue: number;
    /** The highest value the user can select */
    maxValue: number;
    /** The optional starting value. If not provided, minValue is used */
    defaultMinValue?: number;
    /** The optional starting value. If not provided, maxValue is used */
    defaultMaxValue?: number;
    /** The precision of the value captured, in # of decimal places i.e. '2' for .00 */
    decimals?: number;
    /** An optional interval to snap values to. i.e. `snap=5` will snap to 0, 5, 10, etc. */
    step?: number;
    /** The theme variant of this button */
    variant?: ColorVariants;
    /** An optional override for the label on the lefthand side of the control. */
    minValueLabel?: JSX.Element;
    /** An optional override for the label on the righthand side of the control. */
    maxValueLabel?: JSX.Element;
    /** The name of the form field this element is associated with */
    name?: string;
    /** The method that's called when the minimum value changes */
    onMinValueChange?: (newValue: number) => void;
    /** The method that's called when the maximum value changes */
    onMaxValueChange?: (newValue: number) => void;
    /** The custom unit to display after the input fields.
     * e.g. `%`, `mm` */
    unit?: string;
    /** Passthrough for styled-component */
    className?: string;
    /** Whether to allow the minimum value to be edited */
    freezeMinValue?: boolean;
    /** Whether to allow the maximum value to be edited */
    freezeMaxValue?: boolean;
};

const SLIDER_HANDLE_RADIUS = 15;

const SliderBase = ({
    minValue = 0,
    maxValue = 100,
    defaultMinValue,
    defaultMaxValue,
    decimals = 0,
    step,
    minValueLabel,
    maxValueLabel,
    name,
    onMinValueChange,
    onMaxValueChange,
    unit,
    freezeMinValue = false,
    freezeMaxValue = false,
    className,
    variant = 'action',
    ...props
}: SliderProps) => {
    const theme = useTheme() as OSKThemeType;

    // Refs
    const containerRef = useRef<any>(null);

    // Clamp a given value between our min and max
    // and ensure that it's the correct # of significant digits

    const clampValue = (value: number) =>
        decimalClamp(clamp(step ? stepClamp(value, step) : value, minValue, maxValue), decimals);
    // Hooks / ref values
    const [getMinValue, setMinValue] = useRefState<number>(
        defaultMinValue !== undefined && defaultMinValue >= 0 ? clampValue(defaultMinValue) : clampValue(minValue),
    );
    const [getMaxValue, setMaxValue] = useRefState<number>(
        defaultMaxValue !== undefined && defaultMaxValue >= 0 ? clampValue(defaultMaxValue) : clampValue(minValue),
    );

    // Used for updating the page once
    const [, updateState] = React.useState({});
    const update = React.useCallback(() => updateState({}), []);

    // Calculated constants
    const barWidth = containerRef.current ? containerRef.current.offsetWidth : -1;
    const minPerc = (getMinValue() - minValue) / (maxValue - minValue);
    const maxPerc = (getMaxValue() - minValue) / (maxValue - minValue);

    // Force one refresh to populate the refs
    useEffect(() => {
        update();
    }, []);

    // If the default value changes externally, update our value
    useEffect(() => {
        setMinValue(clampValue(defaultMinValue ?? clampValue(minValue)));
    }, [defaultMinValue]);
    useEffect(() => {
        setMaxValue(clampValue(defaultMaxValue ?? clampValue(maxValue)));
    }, [defaultMaxValue]);

    function onResize() {
        update();
    }

    useEffect(() => {
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return (
        <>
            <Box className={className} {...props} col>
                <Box className="slider-line" ref={containerRef} />
                <Box
                    className="slider-fill"
                    style={{
                        left: `calc(${minPerc * barWidth}px + ${SLIDER_HANDLE_RADIUS}px)`,
                        width: `calc(${(maxPerc - minPerc) * barWidth}px)`,
                    }}
                />
                {!freezeMinValue && (
                    <SliderGrabber
                        perc={minPerc}
                        trackWidth={barWidth}
                        onDrag={(delta, finished) => {
                            const percOffset = delta / barWidth;
                            const newPerc = minPerc + percOffset;
                            let newValue = newPerc * (maxValue - minValue) + minValue;
                            newValue = clampValue(newValue);
                            if (newValue > getMaxValue()) newValue = getMaxValue();
                            setMinValue(newValue);

                            if (finished) {
                                onMinValueChange && onMinValueChange(newValue);
                            }
                        }}
                    />
                )}
                {!freezeMaxValue && (
                    <SliderGrabber
                        perc={maxPerc}
                        trackWidth={barWidth}
                        onDrag={(delta, finished) => {
                            const percOffset = delta / barWidth;
                            const newPerc = maxPerc + percOffset;
                            let newValue = newPerc * (maxValue - minValue) + minValue;
                            newValue = parseFloat(newValue.toFixed(decimals));
                            newValue = clampValue(newValue);
                            if (newValue < getMinValue()) newValue = getMinValue();
                            setMaxValue(newValue);

                            if (finished) {
                                onMaxValueChange && onMaxValueChange(newValue);
                            }
                        }}
                    />
                )}
                <Box
                    className="slider-labels"
                    style={{ justifyContent: 'space-between', color: theme.colors.black500 }}
                >
                    <Typography variant="caption3" className="min-max-label" data-testid="slider-minlabel">
                        {minValueLabel ? minValueLabel : `${minValue}${unit}`}
                    </Typography>
                    <Typography variant="heading5" data-testid="slider-value" color={theme.colors[variant].invertedFg}>
                        <Box w="125px" center="all" pt={4} style={{ justifyContent: 'space-around' }}>
                            {!freezeMinValue && (
                                <TextInput
                                    name="min"
                                    data-testid="slider-minvalue"
                                    variant="contrast"
                                    value={getMinValue()}
                                    style={{ maxWidth: '50px' }}
                                    onChange={(e) => {
                                        if (e.target.value && !isNaN(Number(e.target.value))) {
                                            let newValue = clampValue(parseFloat(e.target.value));
                                            if (newValue < getMinValue()) newValue = getMinValue();
                                            setMinValue(newValue);
                                            onMinValueChange && onMinValueChange(newValue);
                                        }
                                    }}
                                />
                            )}
                            {!freezeMinValue && !freezeMaxValue && ' — '}
                            {!freezeMaxValue && (
                                <TextInput
                                    name="max"
                                    data-testid="slider-maxvalue"
                                    variant="contrast"
                                    value={getMaxValue()}
                                    style={{ maxWidth: '50px' }}
                                    onChange={(e) => {
                                        if (e.target.value && !isNaN(Number(e.target.value))) {
                                            let newValue = clampValue(parseFloat(e.target.value));
                                            if (newValue < getMinValue()) newValue = getMinValue();
                                            setMaxValue(newValue);
                                            onMaxValueChange && onMaxValueChange(newValue);
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </Typography>
                    <Typography variant="caption3" className="min-max-label" data-testid="slider-maxlabel">
                        {maxValueLabel ? maxValueLabel : `${maxValue}${unit}`}
                    </Typography>
                </Box>
            </Box>
            {name && <input type="hidden" name={`min-${name}`} value={getMinValue()} />}
            {name && <input type="hidden" name={`max-${name}`} value={getMaxValue()} />}
        </>
    );
};

const Slider = styled(SliderBase)`
    & {
        width: 100%;
        position: relative;
        height: 15px;
        padding: 12px ${SLIDER_HANDLE_RADIUS}px;
        user-drag: none;

        div {
            user-drag: none;
        }
    }

    .slider-line {
        min-width: 50px;
        width: 100%;
        min-height: 5px;
        background-color: ${(props: any) => props.theme.colors.black200};
        border-radius: 10px;
        pointer-events: none;
    }

    .slider-fill {
        height: 5px;
        background-color: ${(props: any) =>
            props.disabled
                ? props.theme.colors.disabled.bg
                : props.bg ?? props.inverted
                ? props.theme.colors[props.variant].invertedBg
                : props.theme.colors[props.variant].bg};
        border-radius: 10px;
        position: absolute;
        pointer-events: none;
    }

    .slider-point-container {
        width: ${SLIDER_HANDLE_RADIUS * 2}px;
        height: ${SLIDER_HANDLE_RADIUS * 2}px;
        position: absolute;
        left: calc(100px - 15px);
        cursor: pointer;
        scale: 100%;
        pointer-events: all;
    }

    .slider-point {
        height: ${SLIDER_HANDLE_RADIUS}px;
        width: ${SLIDER_HANDLE_RADIUS}px;
        border-radius: 25px;
        background-color: ${(props: any) =>
            props.disabled
                ? props.theme.colors.disabled.bg
                : props.bg ?? props.inverted
                ? props.theme.colors[props.variant].invertedBg
                : props.theme.colors[props.variant].bg};
        transition: all 0.5s;
        pointer-events: all;
    }

    .slider-point-container:hover {
        .slider-point {
            filter: drop-shadow(0px 0px 2px #000000cc);
            scale: 150%;
        }
    }

    .slider-labels {
        position: relative;
        min-width: 100%;
        bottom: -4px;
        user-select: none;
    }

    .min-max-label {
    }
`;

Slider.defaultProps = {
    variant: 'action',
};

export { Slider };
